/**
 * Блок «Исполнительные производства ФССП» — переиспользуется карточкой компании
 * (вкладка «Взыскания») и страницей-формой «Проверка по ФССП».
 *
 * Контракт состояний (STATES.md): loading / empty / loaded (+ locked-детали).
 * Принципы:
 *  - Негатив (активные долги, факт оконченных) — всегда FREE, видно без авторизации.
 *  - Под LockedBlock — только детали-доказательства (полный список реквизитов).
 *  - Оконченные показываем честно: count + пояснение, БЕЗ сумм и статьи по записи
 *    (их нет в открытых данных ФССП — не выдумываем).
 *  - Атрибуция (Источник: ФССП России + датасет + дата актуальности) — обязательна.
 */
import { Banner } from './Banner';
import { LockedBlock } from './LockedBlock';
import { EmptyState } from './EmptyState';
import { Skel } from './Skel';
import type { EnforcementItem, EnforcementResult } from '../data/types';

const RUB = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

function money(n: number): string {
  return RUB.format(n);
}

function ruDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU');
}

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface, #ffffff)',
  border: '1px solid var(--border, #e6e2d6)',
  borderRadius: 12,
  padding: 20,
};

const H3_STYLE: React.CSSProperties = {
  fontFamily: 'Fraunces, Georgia, serif',
  fontSize: 16,
  margin: '0 0 12px',
  color: 'var(--navy-700, #0b1736)',
};

function AttributionLine({ a }: { a: EnforcementResult['attribution'] }) {
  const date = ruDate(a.snapshot_date);
  return (
    <div
      style={{
        marginTop: 14,
        fontSize: 12,
        color: 'var(--muted, #6b7287)',
        overflowWrap: 'anywhere',
      }}
    >
      Источник: {a.source} ·{' '}
      <a href={a.source_url} target="_blank" rel="noopener noreferrer">
        открытый банк данных ФССП
      </a>
      {date && <> · актуально на {date}</>}
      <div style={{ marginTop: 4 }}>{a.note}</div>
    </div>
  );
}

function ProceedingCard({ item }: { item: EnforcementItem }) {
  const isActive = item.status === 'active';
  const meta = [
    `№ ${item.id}`,
    item.doc_type,
    item.initiated_at && ruDate(item.initiated_at) ? `от ${ruDate(item.initiated_at)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      style={{
        borderLeft: `4px solid ${isActive ? '#c8323d' : '#b0a99a'}`,
        background: 'var(--surface-quiet, #faf8f2)',
        borderRadius: 8,
        padding: '10px 14px',
        marginBottom: 8,
        opacity: isActive ? 1 : 0.78,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500, overflowWrap: 'anywhere', minWidth: 0, flex: 1 }}>
          {item.subject ?? 'Исполнительное производство'}
        </span>
        {item.debt_remainder != null && (
          <span style={{ fontWeight: 600, color: '#a02631', whiteSpace: 'nowrap' }}>
            {money(item.debt_remainder)}
          </span>
        )}
      </div>
      {meta && (
        <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)', marginTop: 4, overflowWrap: 'anywhere' }}>
          {meta}
        </div>
      )}
      {item.office && (
        <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)', marginTop: 2, overflowWrap: 'anywhere' }}>
          {item.office}
          {item.region ? `, ${item.region}` : ''}
        </div>
      )}
    </div>
  );
}

interface Props {
  data: EnforcementResult | null;
  loading?: boolean;
  isAuthed: boolean;
}

export function EnforcementPanel({ data, loading, isAuthed }: Props) {
  if (loading || !data) {
    return <Skel count={4} height={64} />;
  }

  const { summary, active, completed_preview, attribution, details_locked, has_data } = data;

  if (!has_data) {
    const date = ruDate(attribution.snapshot_date);
    return (
      <section aria-label="Исполнительные производства">
        <EmptyState
          icon="⚖️"
          title="Исполнительных производств не найдено"
          body={`По данным открытого банка ФССП России${date ? ` на ${date}` : ''} у юрлица нет активных и оконченных производств.`}
        />
        <AttributionLine a={attribution} />
      </section>
    );
  }

  const hasActive = summary.active_count > 0;
  const hiddenActive = Math.max(0, summary.active_count - active.length);

  return (
    <section aria-label="Исполнительные производства">
      {/* Сводка — FREE, негатив всегда виден */}
      <Banner tone={hasActive ? 'risk' : 'warn'} title="Исполнительные производства ФССП">
        {hasActive ? (
          <>
            Активных производств: <b>{summary.active_count.toLocaleString('ru-RU')}</b>. Остаток
            долга: <b>{money(summary.active_debt_remainder_rub)}</b>.
          </>
        ) : (
          <>Активных производств нет. В истории есть оконченные: {summary.completed_count.toLocaleString('ru-RU')}.</>
        )}
      </Banner>

      {/* Активные производства — top-N FREE, остальное под замком */}
      {active.length > 0 && (
        <div style={{ ...CARD_STYLE, marginTop: 16 }}>
          <h3 style={H3_STYLE}>
            {isAuthed ? 'Активные производства' : 'Активные производства — примеры'}
          </h3>
          {active.map((item) => (
            <ProceedingCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {details_locked && (
        <div style={{ marginTop: 12 }}>
          <LockedBlock
            title="Полный список исполнительных производств"
            description={
              hiddenActive > 0
                ? `Ещё ${hiddenActive.toLocaleString('ru-RU')} активных производств${summary.completed_count > 0 ? ' и реквизиты оконченных' : ''} — в полном отчёте.`
                : 'Реквизиты оконченных производств — в полном отчёте.'
            }
            ctaHref="#/pricing"
          />
        </div>
      )}

      {/* Оконченные — реквизиты только для авторизованных */}
      {isAuthed && completed_preview.length > 0 && (
        <div style={{ ...CARD_STYLE, marginTop: 16 }}>
          <h3 style={H3_STYLE}>Оконченные производства</h3>
          {completed_preview.map((item) => (
            <ProceedingCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {/* Оконченные — честное пояснение (count виден всем, без сумм/статьи по записи) */}
      {summary.completed_count > 0 && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="info" title={`Оконченные производства: ${summary.completed_count.toLocaleString('ru-RU')}`}>
            Производства окончены (возврат взыскателю по ст. 46 ч. 1 / ст. 47 ФЗ-229, включая
            невозможность взыскания и административные штрафы). Суммы и основания по оконченным
            производствам в открытых данных ФССП не публикуются.
          </Banner>
        </div>
      )}

      <AttributionLine a={attribution} />
    </section>
  );
}
